if (!customElements.get('blog-listing')) {
  // 'newest' is the default, and means published_at - not the order the blog
  // itself comes back in, which follows the last edit. See verifyOrder().
  const SORTS = ['newest', 'oldest', 'alphabetical'];

  // The section's CSS treats 1024 as tablet, so desktop starts one pixel up.
  // theme.mediaQueries.lg is (min-width: 1024px) and would disagree at 1024.
  // 'on:breakpoint-change' does not fire at this boundary either - no theme
  // query crosses it - so anything reading this also listens to it directly.
  const DESKTOP_MQ = window.matchMedia('(min-width: 1025px)');

  class BlogListing extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.resultsEl = this.querySelector('.js-blog-results');
      if (!this.resultsEl) return;

      this.searchInput = this.querySelector('.js-blog-search');
      this.clearBtn = this.querySelector('.js-blog-search-clear');
      this.searchEl = this.querySelector('.blog-listing__search');
      this.toolsEl = this.querySelector('.blog-listing__tools');
      this.tagScroller = this.querySelector('.blog-listing__controls');
      this.sortEl = this.querySelector('.js-blog-sort');
      this.statusEl = this.querySelector('.js-blog-status');
      this.progressEl = this.querySelector('.js-blog-progress');
      this.emptyEl = this.querySelector('.js-blog-empty');
      this.paginationEl = this.querySelector('.js-blog-pagination');

      this.pageSize = parseInt(this.dataset.pageSize, 10) || 16;
      this.pageSizeMobile = parseInt(this.dataset.pageSizeMobile, 10) || this.pageSize;
      this.window = parseInt(this.dataset.window, 10) || 9;
      // Tablet fits fewer numbers than desktop, never more.
      this.windowTablet = Math.min(this.window, parseInt(this.dataset.windowTablet, 10) || 5);
      this.total = parseInt(this.dataset.total, 10) || 0;
      this.indexPageSize = parseInt(this.dataset.indexPageSize, 10) || 50;
      this.indexView = this.dataset.indexView || 'cards';
      this.moreLabel = this.dataset.moreLabel || 'See more';
      this.chevron = this.querySelector('.js-blog-chevron')?.innerHTML.trim() || '';

      // Mobile stacks the pages behind a 'see more' button instead of paging.
      this.isMobile = !window.matchMedia(theme.mediaQueries.md).matches;
      this.isTablet = !DESKTOP_MQ.matches;

      // Populated by ensureIndex(); until then the server-rendered page stands.
      this.cards = null;
      this.indexPromise = null;

      this.readState();

      if (this.searchInput) {
        this.searchInput.value = this.query;
        this.searchInput.addEventListener('input', this.handleSearch.bind(this));
        this.searchInput.addEventListener('focus', () => this.ensureIndex(), { once: true });
      }

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', this.handleClear.bind(this));
        this.clearBtn.classList.toggle('is-visible', this.query.length > 0);
      }

      if (this.sortEl) {
        // custom-select dispatches a bubbling 'change' carrying detail.selectedValue;
        // a native <select> fallback puts the value on the target instead.
        this.sortEl.addEventListener('change', this.handleSort.bind(this));
        this.sortEl.addEventListener('focusin', () => this.ensureIndex(), { once: true });
        this.sortEl.addEventListener('mousedown', () => this.ensureIndex(), { once: true });
        this.syncSortUI();
      }

      this.initToolDrops();

      if (this.tagScroller) this.initTagDrag();

      if (this.paginationEl) {
        this.paginationEl.addEventListener('click', this.handlePageClick.bind(this));
      }

      window.addEventListener('popstate', () => {
        this.readState();
        if (this.searchInput) this.searchInput.value = this.query;
        this.syncSortUI();
        if (this.cards) this.render();
      });

      // Crossing into (or out of) mobile switches stacked pages for paged ones.
      const handleBreakpoint = () => {
        const isMobile = !window.matchMedia(theme.mediaQueries.md).matches;
        const isTablet = !DESKTOP_MQ.matches;
        const tabletChanged = isTablet !== this.isTablet;

        this.isTablet = isTablet;

        if (isMobile === this.isMobile) {
          // Same articles either way, only a narrower run of page numbers. Until
          // the index loads the server's markup stands and CSS handles this.
          if (tabletChanged && !isMobile && this.cards) this.render();
          return;
        }

        this.isMobile = isMobile;

        if (this.cards) {
          this.render();
        } else if (this.trimmed) {
          // The grid is short of a full desktop page - only the index can fill it.
          this.apply();
        }
      };

      window.addEventListener('on:breakpoint-change', handleBreakpoint);
      DESKTOP_MQ.addEventListener('change', handleBreakpoint);

      // A query or sort in the URL means the server-rendered page is wrong for
      // this state, so index straight away rather than waiting for a click. So
      // does a page past the first on mobile, which stacks the pages: without
      // this the earlier articles would appear only on the next 'see more'.
      if (this.query || this.sort !== 'newest' || (this.isMobile && this.page > 1)) {
        this.apply();
      } else {
        if (this.isMobile) this.prepareMobileGrid();
        this.verifyOrder();
      }
    }

    /**
     * Shopify orders blog.articles for itself, and that order follows the last
     * edit rather than the publish date - so an old post edited today arrives
     * at the top of page 1. 'newest' here means published_at, so once the page
     * is idle the whole list is fetched quietly and the grid re-rendered, but
     * only if the two disagree: a blog the server got right pays nothing but
     * the fetch.
     */
    verifyOrder() {
      const check = () => this.ensureIndex(true).then((cards) => {
        if (!cards) return;
        // The user reached for search or sort while this was in flight, and
        // apply() has already rendered whatever they asked for.
        if (this.query || this.sort !== 'newest') return;
        if (this.orderMatches()) return;
        this.render();
      });

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(check, { timeout: 2000 });
      } else {
        window.setTimeout(check, 500);
      }
    }

    /**
     * @returns {boolean} Whether the grid already holds the articles, in the
     *   order, that a published_at sort of the full index would put there.
     */
    orderMatches() {
      const perPage = this.perPage();
      const sorted = this.cards.slice().sort(BlogListing.comparator('newest'));
      // Mirrors render(): mobile keeps every page so far on screen, desktop one.
      const start = this.isMobile ? 0 : (this.page - 1) * perPage;
      const expected = sorted.slice(start, this.page * perPage);
      const current = Array.from(this.resultsEl.querySelectorAll('.js-blog-card'));

      if (expected.length !== current.length) return false;
      return expected.every((card, i) => card.dataset.published === current[i].dataset.published
        && card.dataset.title === current[i].dataset.title);
    }

    /**
     * Reads query, sort and page out of the URL so links stay shareable.
     */
    readState() {
      const params = new URLSearchParams(window.location.search);
      const sort = params.get('sort_by');

      this.query = (params.get('q') || '').trim();
      this.sort = SORTS.includes(sort) ? sort : 'newest';
      this.page = Math.max(1, parseInt(params.get('page'), 10) || 1);
    }

    /**
     * Reflects the current state in the URL without reloading the page.
     * @param {boolean} [push=false] - Add a history entry rather than replacing.
     */
    writeState(push = false) {
      const params = new URLSearchParams(window.location.search);

      if (this.query) params.set('q', this.query); else params.delete('q');
      if (this.sort !== 'newest') params.set('sort_by', this.sort); else params.delete('sort_by');
      if (this.page > 1) params.set('page', this.page); else params.delete('page');

      const search = params.toString();
      const url = `${window.location.pathname}${search ? `?${search}` : ''}`;

      if (push) {
        window.history.pushState({}, '', url);
      } else {
        window.history.replaceState({}, '', url);
      }
    }

    /**
     * Fetches every article once so search and sort can span the whole blog.
     *
     * Liquid caps blog.articles at 50 outside a paginate tag and cannot sort
     * articles at all, so the full set is assembled from the ?view= fragment
     * endpoint - 50 per request, all in flight together.
     * @returns {Promise<?Array>} The card elements, or null if the fetch failed.
     */
    ensureIndex(quiet = false) {
      if (this.cards) return Promise.resolve(this.cards);

      if (this.indexPromise) {
        // A quiet pass got here first. This caller is a click, though, so it
        // gets the wait spelled out even though the fetch is already going.
        if (!quiet && !this.hasAttribute('aria-busy')) {
          this.setStatus(`Loading all ${this.total} articles…`);
          this.setBusy(true);
          this.indexPromise.then(() => this.setBusy(false));
        }
        return this.indexPromise;
      }

      const pages = Math.max(1, Math.ceil(this.total / this.indexPageSize));
      if (!quiet) {
        this.setStatus(`Loading all ${this.total} articles…`);
        this.setBusy(true);
      }

      this.indexPromise = Promise.all(
        Array.from({ length: pages }, (unused, i) => BlogListing.fetchPage(
          `${window.location.pathname}?view=${this.indexView}&page=${i + 1}`
        ))
      )
        .then((chunks) => {
          this.cards = chunks.flat();
          this.cards.forEach((card, i) => { card.dataset.index = i; });
          return this.cards;
        })
        .catch((error) => {
          console.log(error); // eslint-disable-line
          this.indexPromise = null;
          // A quiet pass says nothing when it fails: the server-rendered page
          // stands, and a later click retries it loudly.
          if (!quiet) this.setStatus('Could not load the full article list. Showing this page only.');
          return null;
        })
        .finally(() => this.setBusy(false));

      return this.indexPromise;
    }

    /**
     * @param {string} url - Fragment endpoint URL.
     * @returns {Promise<Array>} Card elements parsed out of the response.
     */
    static async fetchPage(url) {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`${url}: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return Array.from(doc.querySelectorAll('.js-blog-card'));
    }

    /**
     * Loads the index if needed, then re-renders.
     */
    async apply() {
      const cards = await this.ensureIndex();
      if (cards) this.render();
    }

    handleSearch() {
      this.query = this.searchInput.value.trim();
      this.page = 1;
      this.writeState();
      if (this.clearBtn) this.clearBtn.classList.toggle('is-visible', this.query.length > 0);
      this.apply();
    }

    handleClear() {
      this.query = '';
      this.page = 1;
      if (this.searchInput) {
        this.searchInput.value = '';
        this.searchInput.focus();
      }
      if (this.clearBtn) this.clearBtn.classList.remove('is-visible');
      this.writeState();
      this.apply();
    }

    /**
     * Sets up the tablet/mobile dropdowns, where the header has room for the
     * search and sort icons but not the controls themselves.
     */
    initToolDrops() {
      // 'el' is the control to move into 'drop' at tablet and below. The sort
      // has none: it is its own drop, going absolute in the header instead.
      this.toolDrops = [
        {
          el: this.searchEl,
          toggle: this.querySelector('.js-blog-search-toggle'),
          drop: this.querySelector('.js-blog-search-drop'),
          // Focusing the field also starts indexing (its own 'focus' handler).
          onOpen: () => this.searchInput?.focus()
        },
        {
          el: null,
          toggle: this.querySelector('.js-blog-sort-toggle'),
          drop: this.sortEl,
          // One tap should reach the options, not just uncover the pill. The
          // element is looked up each time: custom-select.js owns this markup.
          onOpen: () => this.sortSelect()?.showListbox(),
          onClose: () => this.sortSelect()?.hideListbox()
        }
      ].filter((tool) => tool.toggle && tool.drop);

      this.movableTools = this.toolDrops.filter((tool) => tool.el);

      if (!this.toolDrops.length || !this.toolsEl) return;

      // Where each movable control belongs on desktop, so it goes back to its
      // own slot rather than the end of the row.
      this.movableTools.forEach((tool) => {
        tool.home = tool.el.parentElement;
        tool.homeNext = tool.el.nextElementSibling;
      });

      this.toolDrops.forEach((tool) => {
        tool.toggle.addEventListener('click', this.handleToolToggle.bind(this, tool));
        tool.drop.addEventListener('keyup', this.handleToolDropKeyup.bind(this, tool));
      });

      document.addEventListener('click', this.handleDocClick.bind(this));
      const placeTools = this.placeTools.bind(this);
      window.addEventListener('on:breakpoint-change', placeTools);
      DESKTOP_MQ.addEventListener('change', placeTools);

      this.placeTools();

      // A query from the URL is invisible with the field tucked away, so show it
      // - but without stealing focus (and scroll position) on load.
      const search = this.searchEl && this.movableTools.find((tool) => tool.el === this.searchEl);
      if (this.query && search && this.searchEl.parentElement === search.drop) {
        this.openToolDrop(search, false);
      }
    }

    /**
     * Puts the movable controls inline in the header on desktop and in their
     * dropdowns below tablet. They are moved rather than duplicated, so their
     * values, listeners and labels survive the change.
     */
    placeTools() {
      const isDesktop = DESKTOP_MQ.matches;

      if (isDesktop) {
        this.movableTools.forEach((tool) => {
          // Re-inserting a control that is already home would drop focus mid-typing.
          if (tool.el.parentElement === tool.home) return;

          const before = tool.homeNext?.parentElement === tool.home ? tool.homeNext : null;
          tool.home.insertBefore(tool.el, before);
        });

        this.closeToolDrops();
        return;
      }

      this.movableTools.forEach((tool) => {
        if (tool.el.parentElement !== tool.drop) tool.drop.appendChild(tool.el);
      });
    }

    /**
     * @param {object} tool - Entry from this.toolDrops.
     * @param {object} evt - 'click' on that entry's icon.
     */
    handleToolToggle(tool, evt) {
      evt.preventDefault();

      if (tool.drop.classList.contains('is-open')) {
        this.closeToolDrops(true);
      } else {
        this.openToolDrop(tool);
      }
    }

    /**
     * @param {object} tool - Entry from this.toolDrops.
     * @param {object} evt - 'keyup' inside that entry's dropdown.
     */
    handleToolDropKeyup(tool, evt) {
      if (evt.key !== 'Escape') return;

      this.closeToolDrops(true);
    }

    /**
     * Closes the dropdowns on a click outside of them.
     * @param {object} evt - 'click' on the document.
     */
    handleDocClick(evt) {
      const open = this.toolDrops.filter((tool) => tool.drop.classList.contains('is-open'));
      if (!open.length) return;
      if (open.some((tool) => tool.drop.contains(evt.target) || tool.toggle.contains(evt.target))) return;

      this.closeToolDrops();
    }

    /**
     * Opens one dropdown, closing the other - they share the same strip of space.
     * @param {object} tool - Entry from this.toolDrops.
     * @param {boolean} [reveal=true] - Hand the control focus / open its options.
     */
    openToolDrop(tool, reveal = true) {
      this.closeToolDrops();

      tool.drop.classList.add('is-open');
      tool.toggle.setAttribute('aria-expanded', 'true');

      // Deferred: the control cannot take focus until the panel is visible.
      if (reveal) setTimeout(() => tool.onOpen?.());
    }

    /**
     * @param {boolean} [returnFocus=false] - Send focus back to the icon. Closing
     *   otherwise leaves it on a control that has just gone hidden.
     */
    closeToolDrops(returnFocus = false) {
      // Optional: handleSort can fire before the dropdowns are set up.
      this.toolDrops?.forEach((tool) => {
        if (!tool.drop.classList.contains('is-open')) return;

        tool.onClose?.();
        tool.drop.classList.remove('is-open');
        tool.toggle.setAttribute('aria-expanded', 'false');

        if (returnFocus) tool.toggle.focus();
      });
    }

    /**
     * Drag-to-scroll for the tag strip, which hides its scrollbar. Mouse only:
     * a touch pointer already scrolls it by dragging.
     */
    initTagDrag() {
      if (!window.matchMedia('(pointer: fine)').matches) return;

      this.tagScroller.addEventListener('mousedown', this.handleTagMousedown.bind(this));
      this.tagScroller.addEventListener('mousemove', this.handleTagMousemove.bind(this));

      const endDrag = this.handleTagMouseup.bind(this);
      this.tagScroller.addEventListener('mouseup', endDrag);
      this.tagScroller.addEventListener('mouseleave', endDrag);

      // Whether there is anything to drag depends on the width available.
      this.tagGrabHandler = this.setTagGrabbable.bind(this);
      window.addEventListener('on:debounced-resize', this.tagGrabHandler);
      this.setTagGrabbable();
    }

    setTagGrabbable() {
      const overflows = this.tagScroller.scrollWidth > this.tagScroller.clientWidth;
      this.tagScroller.classList.toggle('is-grabbable', overflows);
    }

    /**
     * @param {object} evt - 'mousedown' on the tag strip.
     */
    handleTagMousedown(evt) {
      if (!this.tagScroller.classList.contains('is-grabbable')) return;

      this.tagDrag = { x: evt.pageX, scrollLeft: this.tagScroller.scrollLeft };
      this.tagScroller.classList.add('is-grabbing');
    }

    /**
     * @param {object} evt - 'mousemove' over the tag strip.
     */
    handleTagMousemove(evt) {
      if (!this.tagDrag) return;

      // Ignore jitter, so a click on a tag still reaches the tag.
      const delta = evt.pageX - this.tagDrag.x;
      if (Math.abs(delta) < 5) return;

      evt.preventDefault();
      this.tagScroller.scrollLeft = this.tagDrag.scrollLeft - delta;

      // Stops the drag ending in a click through to whichever tag is under it.
      this.tagScroller.classList.add('is-dragging');
    }

    handleTagMouseup() {
      this.tagDrag = null;
      this.tagScroller.classList.remove('is-grabbing', 'is-dragging');
    }

    /**
     * @returns {?Element} The sort's custom-select, once it has been upgraded.
     */
    sortSelect() {
      const select = this.sortEl?.querySelector('custom-select');
      return select?.showListbox ? select : null;
    }

    /**
     * @param {object} evt - 'change' from custom-select or a native <select>.
     */
    handleSort(evt) {
      const value = evt.detail?.selectedValue ?? evt.target.value;

      // Picking an option is the end of the interaction - put the pill away.
      this.closeToolDrops(true);

      if (!SORTS.includes(value) || value === this.sort) return;

      this.sort = value;
      this.page = 1;
      this.writeState();
      this.apply();
    }

    /**
     * Points the sort control at this.sort.
     *
     * Liquid renders the control with the default option selected because it
     * cannot read ?sort_by= from the URL, so the button label has to be
     * corrected here on load and on history navigation.
     */
    syncSortUI() {
      if (!this.sortEl) return;

      const native = this.sortEl.querySelector('select');
      if (native) native.value = this.sort;

      const option = this.sortEl.querySelector(`.js-option[data-value="${this.sort}"]`);
      if (!option) return;

      this.sortEl.querySelectorAll('.js-option[aria-selected]').forEach((el) => {
        el.removeAttribute('aria-selected');
      });
      option.setAttribute('aria-selected', 'true');

      const button = this.sortEl.querySelector('.custom-select__btn');
      if (button?.firstElementChild) {
        button.firstElementChild.textContent = option.firstElementChild.textContent;
      }

      this.sortEl.querySelector('.custom-select__listbox')
        ?.setAttribute('aria-activedescendant', option.id);
    }

    /**
     * @param {object} evt - Click event from the pagination container.
     */
    handlePageClick(evt) {
      const target = evt.target.closest('[data-page]');
      if (!target) return;

      const page = parseInt(target.dataset.page, 10);
      if (!page) return;

      // 'See more' adds to the grid, so it never navigates: on the first click
      // it loads the index first rather than reloading the page.
      if (target.matches('.js-blog-more')) {
        evt.preventDefault();

        this.page = page;
        this.restoreMoreFocus = true;
        this.writeState(true);

        if (this.cards) this.render(); else this.apply();
        return;
      }

      // Before the index loads the page links are real hrefs - let the browser go.
      if (!this.cards) return;

      evt.preventDefault();
      if (page === this.page) return;

      this.page = page;
      this.writeState(true);
      this.render();

      this.resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /**
     * Filters, sorts and pages the indexed cards, then swaps them into the grid.
     */
    render() {
      const needle = this.query.toLowerCase();
      const matches = needle
        ? this.cards.filter((card) => (card.dataset.search || '').includes(needle))
        : this.cards.slice();

      matches.sort(BlogListing.comparator(this.sort));

      const perPage = this.perPage();
      const totalPages = Math.max(1, Math.ceil(matches.length / perPage));
      if (this.page > totalPages) {
        this.page = totalPages;
        this.writeState();
      }

      // Mobile keeps every page loaded so far on screen; desktop shows one.
      const start = this.isMobile ? 0 : (this.page - 1) * perPage;
      const visible = matches.slice(start, this.page * perPage);

      this.resultsEl.replaceChildren(...visible);
      this.renderStatus(matches.length, visible.length, start);
      this.renderPagination(totalPages);

      if (this.emptyEl) this.emptyEl.hidden = matches.length > 0;
    }

    /**
     * @param {number} total - Matching article count.
     * @param {number} shown - Count on the current page.
     * @param {number} start - Zero-based index of the first visible card.
     */
    renderStatus(total, shown, start) {
      this.renderProgress(total, shown);

      if (total === 0) {
        this.setStatus('');
        return;
      }

      if (this.isMobile) {
        // Stacked pages always start at the first, so a range adds nothing.
        this.setStatus(`Showing ${shown} of ${total}`);
        return;
      }

      const noun = total === 1 ? 'article' : 'articles';
      this.setStatus(total <= this.perPage()
        ? `${total} ${noun}`
        : `Showing ${start + 1}-${start + shown} of ${total} ${noun}`);
    }

    /**
     * The same count as a bar: how much of the list is on screen. Hidden once
     * everything is, where a full bar would say nothing the list does not.
     * @param {number} total - Matching article count.
     * @param {number} shown - Count currently in the grid.
     */
    renderProgress(total, shown) {
      if (!this.progressEl) return;

      this.progressEl.hidden = total === 0 || shown >= total;
      if (this.progressEl.hidden) return;

      this.progressEl.firstElementChild.style.width = `${(shown / total) * 100}%`;
    }

    /**
     * @returns {number} Articles per page at the current breakpoint.
     */
    perPage() {
      return this.isMobile ? this.pageSizeMobile : this.pageSize;
    }

    /**
     * @returns {number} How many page numbers fit at the current breakpoint.
     */
    pageWindow() {
      return this.isTablet ? this.windowTablet : this.window;
    }

    /**
     * @param {string} text - Message to show, or empty to hide the element.
     */
    setStatus(text) {
      if (!this.statusEl) return;
      this.statusEl.textContent = text;
      this.statusEl.hidden = !text;
    }

    /**
     * @param {boolean} busy - Whether the index is loading.
     */
    setBusy(busy) {
      this.classList.toggle('is-loading', busy);
      if (busy) {
        this.setAttribute('aria-busy', 'true');
      } else {
        this.removeAttribute('aria-busy');
      }
    }

    /**
     * Builds the page links: a fixed-width window centred on the current page,
     * clamped to the ends, with first/last always present and an ellipsis for
     * any gap. Mirrors the Liquid version this replaces.
     * @param {number} totalPages - Total page count.
     */
    renderPagination(totalPages) {
      if (!this.paginationEl) return;

      if (this.isMobile) {
        this.renderMore(totalPages);
        return;
      }

      if (totalPages < 2) {
        this.paginationEl.innerHTML = '';
        return;
      }

      const size = this.pageWindow();
      let windowStart = 1;
      let windowEnd = totalPages;

      if (totalPages > size) {
        const half = Math.floor(size / 2);
        windowStart = this.page - half;
        windowEnd = this.page + half;

        if (windowStart < 1) {
          windowStart = 1;
          windowEnd = size;
        }

        if (windowEnd > totalPages) {
          windowEnd = totalPages;
          windowStart = totalPages - size + 1;
        }
      }

      const items = [this.arrow('prev', this.page > 1 ? this.page - 1 : null)];

      if (windowStart > 1) {
        items.push(BlogListing.pageItem(1, this.page));
        if (windowStart > 2) items.push(BlogListing.ellipsis());
      }

      for (let page = windowStart; page <= windowEnd; page += 1) {
        items.push(BlogListing.pageItem(page, this.page));
      }

      if (windowEnd < totalPages) {
        if (windowEnd < totalPages - 1) items.push(BlogListing.ellipsis());
        items.push(BlogListing.pageItem(totalPages, this.page));
      }

      items.push(this.arrow('next', this.page < totalPages ? this.page + 1 : null));

      this.paginationEl.innerHTML = `
        <nav class="blog-pagination" aria-label="Pagination">
          <ul class="blog-pagination__list">${items.join('')}</ul>
        </nav>
      `;
    }

    /**
     * Mobile's replacement for the page links: one button that adds the next
     * page below what is already shown. Built rather than templated, so the
     * merchant's label can never be read as markup.
     * @param {number} totalPages - Total page count.
     */
    renderMore(totalPages) {
      // Read and clear together: on the last page there is no button to focus,
      // and a pending flag would then fire on some later, unrelated render.
      const restoreFocus = this.restoreMoreFocus;
      this.restoreMoreFocus = false;

      this.paginationEl.replaceChildren();
      if (this.page >= totalPages) return;

      const wrapper = this.moreButton(this.page + 1);
      this.paginationEl.appendChild(wrapper);

      // The button that was clicked has just been replaced, taking focus with it.
      if (restoreFocus) wrapper.firstElementChild.focus();
    }

    /**
     * Built rather than templated, so the merchant's label can never be read
     * as markup.
     * @param {number} page - Page the button loads.
     * @returns {Element} The button, in its layout wrapper.
     */
    moreButton(page) {
      const wrapper = document.createElement('div');
      wrapper.className = 'blog-listing__more';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'blog-listing__more-btn js-blog-more';
      button.dataset.page = page;
      button.textContent = this.moreLabel;

      wrapper.appendChild(button);
      return wrapper;
    }

    /**
     * Cuts the server-rendered grid down to the mobile page size and fills in
     * the count below it. Liquid has to paginate by one number and cannot know
     * the viewport, so it uses the desktop one - trimming here beats fetching
     * the whole index just to show fewer articles.
     */
    prepareMobileGrid() {
      const cards = Array.from(this.resultsEl.querySelectorAll('.js-blog-card'));
      const perPage = this.perPage();

      if (cards.length > perPage) {
        cards.slice(perPage).forEach((card) => card.remove());

        // Flags the grid as no longer matching what the server sent, so crossing
        // back to desktop knows it has to rebuild rather than leave it short.
        this.trimmed = true;

        // Liquid sizes its button off its own page count, so there may be none.
        if (this.paginationEl && !this.paginationEl.querySelector('.js-blog-more')) {
          this.paginationEl.appendChild(this.moreButton(this.page + 1));
        }
      }

      // The server renders no count, and mobile shows one. data-total covers the
      // whole blog, which is what is being counted towards until a search narrows it.
      this.renderStatus(this.total, Math.min(cards.length, perPage), 0);
    }

    /**
     * @param {string} sort - One of SORTS.
     * @returns {Function} Array sort comparator.
     */
    static comparator(sort) {
      switch (sort) {
        case 'oldest':
          return (a, b) => a.dataset.published - b.dataset.published
            || a.dataset.index - b.dataset.index;
        case 'alphabetical':
          return (a, b) => (a.dataset.title || '').localeCompare(b.dataset.title || '');
        default:
          // 'newest' - newest published_at first, whatever order the server
          // handed the cards over in.
          return (a, b) => b.dataset.published - a.dataset.published
            || a.dataset.index - b.dataset.index;
      }
    }

    /**
     * @param {string} direction - Either 'prev' or 'next'.
     * @param {?number} page - Target page, or null when disabled.
     * @returns {string} List item markup.
     */
    arrow(direction, page) {
      const label = direction === 'prev' ? 'Previous page' : 'Next page';
      const classes = `blog-pagination__arrow blog-pagination__arrow--${direction}`;
      const icon = `<span class="svg-wrapper" aria-hidden="true">${this.chevron}</span>`;

      if (!page) {
        return `<li><span class="${classes} is-disabled" role="link" aria-disabled="true">${icon}</span></li>`;
      }
      return `<li><a class="${classes}" href="#" data-page="${page}" aria-label="${label}">${icon}</a></li>`;
    }

    /**
     * @param {number} page - Page this link targets.
     * @param {number} current - Current page.
     * @returns {string} List item markup.
     */
    static pageItem(page, current) {
      if (page === current) {
        return `<li><span class="blog-pagination__page is-current" aria-current="page">${page}</span></li>`;
      }
      return `<li><a class="blog-pagination__page" href="#" data-page="${page}">${page}</a></li>`;
    }

    /**
     * @returns {string} List item markup.
     */
    static ellipsis() {
      return '<li><span class="blog-pagination__ellipsis" aria-hidden="true">&hellip;</span></li>';
    }
  }

  customElements.define('blog-listing', BlogListing);
}
